import { InputField } from "@/components/input/InputField";
import { Button } from "@/components/ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACTIVITY_OPTIONS, UNITS_OPTIONS } from "@/constants/xactimate";
import type { IXactimateUserRowPayload } from "@/interfaces/payloads/xactimate";
import { XACTIMATE_CATEGORIES } from "@rebuild/shared";
import type { FormulaUnit } from "@rebuild/shared";
import { useMemo, useState } from "react";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { QuantityInput } from "./QuantityInput";

interface CreateLineItemFormProps {
  lineItemText?: string;
  onCreateLineItem: (mapping: IXactimateUserRowPayload, quantityString?: string) => boolean | Promise<boolean>;
  setShowCreateItem: (show: boolean) => void;
}

interface FormErrors {
  acceptedLineString?: string;
  cat?: string;
  variable?: string;
  activity?: string;
  quantity?: string;
  quantityString?: string;
}

export default function CreateLineItemForm({ onCreateLineItem, lineItemText, setShowCreateItem }: CreateLineItemFormProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const allowInvalidFormulas = isFeatureEnabled("ALLOW_USER_TO_SAVE_INVALID_FORMULAS") || true;
  const [formData, setFormData] = useState<IXactimateUserRowPayload>({
    cat: "",
    variable: "",
    quantity: "",
    acceptedLineString: lineItemText ?? "",
    activity: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [quantityString, setQuantityString] = useState<string>("");

  const catOptions = useMemo(() => {
    return XACTIMATE_CATEGORIES.map((cat) => ({ label: cat, value: cat }));
  }, []);

  const validateField = (field: keyof IXactimateUserRowPayload, value: string) => {
    let error = "";
    switch (field) {
      case "acceptedLineString":
        if (!value?.trim()) {
          error = "Line item text is required";
        }
        break;
      case "cat":
        if (!value) {
          error = "Category is required";
        }
        break;
      case "variable":
        if (!value?.trim()) {
          error = "Variable is required";
        }
        break;
      case "quantity":
        if (!value) {
          error = "Unit is required";
        }
        break;
      case "activity":
        if (!value) {
          error = "Activity is required";
        }
        break;
    }
    return error;
  };

  const handleFieldChange = (field: keyof IXactimateUserRowPayload, value: string | React.ChangeEvent<HTMLInputElement>) => {
    const actualValue = typeof value === "string" ? value : value.target.value;

    // Update form data
    setFormData((prev) => ({ ...prev, [field]: actualValue }));

    // Validate and update error for this field
    const error = validateField(field, actualValue);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Validate all fields
    (Object.keys(formData) as Array<keyof IXactimateUserRowPayload>).forEach((field) => {
      const error = validateField(field, formData[field] as string);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    if (errors.quantityString) {
      newErrors.quantityString = errors.quantityString;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Create the line item with the form data
    const created = await onCreateLineItem(
      {
        ...formData,
        acceptedLineString: formData.acceptedLineString?.trim() ?? "",
        variable: formData.variable?.trim() ?? "",
      },
      quantityString || undefined,
    );

    // Keep the form open on failure so the user can retry without losing input
    if (created) {
      setShowCreateItem(false);
    }
  };

  return (
    <div className="border-b border-[#EEEFF1] px-4 py-3">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <InputField
          name="line-item-text"
          label="Line Item Text"
          placeHolder="Line item text here"
          value={formData.acceptedLineString}
          onChange={(value) => handleFieldChange("acceptedLineString", value)}
          error={errors.acceptedLineString}
        />
        <div className="flex flex-row gap-3">
          <div className="flex w-1/5 flex-col gap-1">
            <div className="text-sm font-medium">CAT</div>
            <Combobox
              items={catOptions}
              itemToStringValue={(option) => option.label}
              value={catOptions.find((opt) => opt.value === formData.cat) ?? null}
              onValueChange={(item) => handleFieldChange("cat", item?.value ?? "")}
            >
              <ComboboxInput
                showClear={!!formData.cat}
                placeholder="Select CAT"
                className={errors.cat ? "border-orange-500 bg-orange-50" : ""}
              />
              <ComboboxContent className="pointer-events-auto">
                <ComboboxEmpty>No results found</ComboboxEmpty>
                <ComboboxList className="max-h-60 overflow-y-auto">
                  {(option) => (
                    <ComboboxItem key={option.value} value={option}>
                      {option.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            {errors.cat && <p className="text-sm text-orange-500">{errors.cat}</p>}
          </div>
          <div className="flex w-1/5 flex-col gap-1">
            <div className="text-sm font-medium">SEL</div>
            <InputField
              name="line-item-sel"
              value={formData.variable}
              onChange={(value) => handleFieldChange("variable", value)}
              error={errors.variable}
            />
          </div>
          <div className="flex w-1/5 flex-col gap-1">
            <div className="text-sm font-medium">Activity</div>
            <Select value={formData.activity} onValueChange={(value) => handleFieldChange("activity", value)}>
              <SelectTrigger className={errors.activity ? "border-orange-500 bg-orange-50" : ""}>
                <SelectValue placeholder="Select Activity" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.activity && <p className="text-sm text-orange-500">{errors.activity}</p>}
          </div>
          <div className="flex w-1/5 flex-col gap-1">
            <div className="text-sm font-medium">Units</div>
            <Select value={formData.quantity} onValueChange={(value) => handleFieldChange("quantity", value)}>
              <SelectTrigger className={errors.quantity ? "border-orange-500 bg-orange-50" : ""}>
                <SelectValue placeholder="Select a unit" />
              </SelectTrigger>
              <SelectContent>
                {UNITS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.quantity && <p className="text-sm text-orange-500">{errors.quantity}</p>}
          </div>
          <div className="flex w-1/5 flex-col gap-1">
            <div className="text-sm font-medium">QTY</div>
            <QuantityInput
              onChange={(e) => setQuantityString(e.target.value)}
              onValidityChange={(isValid) => setErrors((prev) => ({ ...prev, quantityString: isValid ? undefined : "Invalid quantity" }))}
              correctUnit={formData.quantity as FormulaUnit}
              disabled={!formData.quantity}
              className=""
              inputClassName="h-9 w-full shadow-none"
              allowInvalidFormulas={allowInvalidFormulas}
            />
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <Button type="button" variant="outline" onClick={() => setShowCreateItem(false)}>
            Cancel
          </Button>
          <Button variant="default" type="submit">
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
